import { Component, OnInit } from '@angular/core';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';

import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { RouterModule, ActivatedRoute } from '@angular/router';
import { MatExpansionModule } from '@angular/material/expansion';
import { SseForm } from '../../../services/sse/sse-index.interface';
import { SseFormService } from '../../../services/sse/sseForm.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';

@Component({
    selector: 'app-sse-index',
    imports: [
    MatProgressBarModule,
    MatButtonModule,
    MatCheckboxModule,
    MatInputModule,
    MatRadioModule,
    MatFormFieldModule,
    MatCardModule,
    FormsModule,
    ReactiveFormsModule,
    MatSelect,
    MatOption,
    MatDatepickerModule,
    RouterModule,
    MatExpansionModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatMenuModule
],
    templateUrl: './sse-index.component.html',
    styleUrl: './sse-index.component.css'
})
export class SseIndexComponent implements OnInit {
  currentStep = 1;
  formData: SseForm = {
    section1: {
      hrName: '',
      hrDate: '',
      hrTitle: '',
      hrTime: '',
      hrExp: '',
      hrDesc: '',
      hrEmail: '',
      hrOfficeEmail: '',
      hrCompleted: false,
    },
    section2: {
      ccName: '',
      ccDate: '',
      ccTitle: '',
      ccTime: '',
      ccExp: '',
      ccDesc: '',
      ccEmail: '',
      ccNameSign: '',
      ccSignDate: '',
      ccCompleted: false,
    },
    section3: {
      smConfirm1: '',
      smConfirm2: '',
      smConfirm3: '',
      smCompleted: false,
    },
  };
  formId: string | null = null;
  sseListId: string | null = null;
  loadingSection1 = false;
  loadingSection2 = false;
  loadingSection3 = false;

  constructor(private sseFormService: SseFormService, private route: ActivatedRoute) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.formId = params.get('id'); 
      if (this.formId) {
        this.loadForm(this.formId);
      } else {
        this.initializeNewForm();
        this.setCurrentStep(); 
      }
    });
  }
  
  loadForm(formId: string): void {
    this.sseFormService.getForm(formId).subscribe(
      (response) => {
        this.formData = response['data'];
        this.setCurrentStep(); 
        this.loadSseList(formId); 
      },
      (error) => {
        console.error('Error fetching form data:', error);
      }
    );
  }
  
  loadSseList(formId: string): void {
    this.sseFormService.findAllSseList().subscribe(
      (response) => {
        const sseLists = response?.data || [];
        const matchingList = sseLists.find((list: any) => list.indexId === formId);
        this.sseListId = matchingList?._id || null;
      },
      (error) => {
        console.error('Error fetching SSE list data:', error);
      }
    );
  }

  initializeNewForm(): void {
    this.formData = {
      section1: {
        hrName: '',
        hrDate: '',
        hrTitle: '',
        hrTime: '',
        hrExp: '',
        hrDesc: '',
        hrEmail: '',
        hrOfficeEmail: '',
        hrCompleted: false,
      },
      section2: null, 
      section3: null, 
    };
    this.currentStep = 1; 
  }

setCurrentStep(): void {
  if (this.formData.section3?.smCompleted) {
    this.currentStep = null; 
  } else if (this.formData.section1?.hrCompleted && !this.formData.section2?.ccCompleted) {
    this.currentStep = 2; 
  } else if (this.formData.section2?.ccCompleted && !this.formData.section3?.smCompleted) {
    this.currentStep = 3; 
  } else {
    this.currentStep = 1; 
  }
}

  nextStep(): void {
    if (this.currentStep === 1) {
      this.saveSection1();
    } else if (this.currentStep === 2) {
      this.saveSection2();
    } else if (this.currentStep === 3) {
      this.saveSection3();
    }
  }

  saveSection1(): void {
    this.loadingSection1 = true;
    this.sseFormService.createForm(this.formData).subscribe(
      (response) => {
        this.formId = response.data._id; 
        this.formData!.section1.hrCompleted = true; 
        this.loadingSection1 = false;
        this.moveToNextStep();
      },
      (error) => {
        console.error('Error saving section 1:', error);
        this.loadingSection1 = false;
      }
    );
  }

  saveSection2(): void {
    if (this.formData.section2?.ccCompleted) {
        console.warn('Section 2 is already completed. Cannot re-submit.');
        return;
    }
    this.loadingSection2 = true;
    this.sseFormService.updateSection2(this.formId, this.formData.section2).subscribe(
        (response) => {
            this.formData.section2.ccCompleted = true; 
            this.loadingSection2 = false;
            this.moveToNextStep();
        },
        (error) => {
            console.error('Error saving section 2:', error);
            this.loadingSection2 = false;
        }
    );
}

saveSection3(): void {
    if (this.formData.section3?.smCompleted) {
        console.warn('Section 3 is already completed. Cannot re-submit.');
        return;
    }
    this.loadingSection3 = true; 
    this.sseFormService.updateSection3(this.formId, this.formData.section3).subscribe(
        () => {
            this.formData.section3.smCompleted = true;
            this.loadingSection3 = false;
        },
        (error) => {
            console.error('Error saving section 3:', error);
            this.loadingSection3 = false;
        }
    );
}

  moveToNextStep(): void {
    if (this.currentStep <= 3) {
      this.currentStep++;
    }
  }

  calculateProgress(): number {
    const completedSections = [
      this.formData?.section1?.hrCompleted,
      this.formData?.section2?.ccCompleted,
      this.formData?.section3?.smCompleted,
    ].filter(Boolean).length;
    return (completedSections / 3) * 100;
  }
}
